import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Plus, RefreshCw, TrendingUp, Users, Wallet } from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import { HBarList } from '../components/charts.jsx';
import { Badge, Button, Card, CardBody, CardHead, cx, EmptyState, Field, Input, Loading, Modal, PhoneInput, SearchInput, Select, Stat, useToast } from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { compactMoney, money, num, titleCase } from '../lib/format.js';

const KINDS = [
  { value: 'all', label: 'All types' },
  { value: 'shipper', label: 'Shippers' },
  { value: 'receiver', label: 'Receivers / DCs' },
  { value: 'broker', label: 'Brokers' },
  { value: 'carrier', label: 'Carriers' },
  { value: '3pl', label: '3PLs' },
];

const SORTS = [
  { value: 'revenue:desc', label: 'Revenue · 30d' },
  { value: 'margin:desc', label: 'Margin · 30d' },
  { value: 'balance:desc', label: 'Open balance' },
  { value: 'jobs:desc', label: 'Job volume' },
  { value: 'name:asc', label: 'Name' },
];

export default function Customers() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);
  const [addOpen, setAddOpen] = useState(false);

  const kind = params.get('kind') || 'all';
  const sort = params.get('sort') || 'revenue:desc';
  const hasBalance = params.get('balance') === 'true';
  const [sortKey, sortDir] = sort.split(':');

  const filters = useMemo(
    () => ({ kind, sort: sortKey, dir: sortDir, q: debounced || undefined, hasBalance: hasBalance ? 'true' : undefined, limit: 120 }),
    [kind, sortKey, sortDir, debounced, hasBalance],
  );

  const { data, loading, refresh } = useApi('/customers', { params: filters });
  const customers = data || [];

  const totals = customers.reduce(
    (acc, c) => ({
      revenue: acc.revenue + Number(c.revenue30d || 0),
      margin: acc.margin + Number(c.margin30d || 0),
      balance: acc.balance + Number(c.openBalance || 0),
      jobs: acc.jobs + Number(c.jobs30d || 0),
    }),
    { revenue: 0, margin: 0, balance: 0, jobs: 0 },
  );

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHead
        title="Customers"
        subtitle="Everybody who sends us work: the warehouses, the brokers and the trucking companies."
        icon={Building2}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setAddOpen(true)}>Add customer</Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Charged in 30 days" icon={TrendingUp} value={compactMoney(totals.revenue)} tone="amber" foot={`${num(totals.jobs)} jobs`} />
        <Stat label="Left over in 30 days" icon={Wallet} value={compactMoney(totals.margin)} tone="green" foot={totals.revenue ? `${Math.round((totals.margin / totals.revenue) * 100)}% of billings` : '—'} />
        <Stat label="Money owed to us" icon={Wallet} value={compactMoney(totals.balance)} tone={totals.balance > 0 ? 'red' : 'green'} foot="across these accounts" />
        <Stat label="Accounts" icon={Users} value={num(customers.length)} tone="blue" foot={`${customers.filter((c) => c.openDisputes).length} with open disputes`} />
      </div>

      <div className="split mb-20">
        <Card>
          <CardHead title="Who we earn the most from" subtitle="Trailing 30 days" icon={TrendingUp} />
          <CardBody>
            <HBarList
              rows={customers.slice(0, 8).map((c) => ({ label: c.name, value: c.revenue30d }))}
              format={(v) => compactMoney(v)}
            />
          </CardBody>
        </Card>
        <Card>
          <CardHead title="Mix by account type" icon={Building2} />
          <CardBody>
            <HBarList
              tone="blue"
              rows={KINDS.slice(1).map((k) => ({
                label: k.label,
                value: customers.filter((c) => c.kind === k.value).reduce((s, c) => s + Number(c.revenue30d || 0), 0),
              })).filter((r) => r.value)}
              format={(v) => compactMoney(v)}
            />
          </CardBody>
        </Card>
      </div>

      <div className="filter-bar">
        <SearchInput value={query} onChange={setQuery} placeholder="Customer, code, city…" style={{ minWidth: 240, maxWidth: 320 }} />
        <Select className="select-sm" value={kind} onChange={(e) => setParam('kind', e.target.value)} options={KINDS} />
        <button
          type="button"
          className={cx('saved-view', hasBalance && 'active')}
          onClick={() => setParam('balance', hasBalance ? '' : 'true')}
        >
          With open balance
        </button>
        <div className="grow" />
        <Select
          className="select-sm"
          value={sort}
          onChange={(e) => setParam('sort', e.target.value)}
          options={SORTS}
        />
      </div>

      {loading && !customers.length ? (
        <Loading rows={8} />
      ) : customers.length ? (
        <Card>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th className="narrow">Type</th>
                  <th className="narrow">Terms</th>
                  <th className="right">Places</th>
                  <th className="right">Jobs (30 days)</th>
                  <th className="right">We charged</th>
                  <th className="right">Left over</th>
                  <th className="right">Still owed</th>
                  <th className="narrow">Needs someone</th>
                  <th className="narrow">Who looks after them</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} className="clickable" onClick={() => navigate(`/app/customers/${customer.id}`)}>
                    <td>
                      <div className="col gap-2">
                        <span className="row gap-6">
                          <span style={{ fontWeight: 600 }}>{customer.name}</span>
                          {customer.openDisputes ? <Badge tone="red" size="sm">{customer.openDisputes} dispute{customer.openDisputes > 1 ? 's' : ''}</Badge> : null}
                        </span>
                        <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                          {customer.code} · {customer.city}, {customer.state} · {customer.rateCard} rate card
                        </span>
                      </div>
                    </td>
                    <td className="narrow"><Badge tone="muted" size="sm">{titleCase(customer.kind)}</Badge></td>
                    <td className="narrow mono dim">{customer.terms}</td>
                    <td className="right num">{customer.siteCount}</td>
                    <td className="right num">{num(customer.jobs30d)}</td>
                    <td className="right num">{money(customer.revenue30d)}</td>
                    <td className="right num" style={{ color: 'var(--green)' }}>{money(customer.margin30d)}</td>
                    <td className="right num" style={{ color: Number(customer.openBalance) > 0 ? 'var(--amber)' : undefined }}>
                      {money(customer.openBalance)}
                    </td>
                    <td className="narrow">
                      {customer.openJobs ? <Badge tone="amber" size="sm">{customer.openJobs} open</Badge> : <span className="dimmer">—</span>}
                    </td>
                    <td className="narrow dim truncate">{customer.accountOwner || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card><EmptyState icon={Building2} title="No customers match this view" /></Card>
      )}

      <AddCustomerModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(customer) => {
          toast.ok(`${customer.name} added`, 'Add their facilities next so you can post work.');
          refresh();
          navigate(`/app/customers/${customer.id}`);
        }}
      />
    </>
  );
}

function AddCustomerModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    kind: 'shipper',
    billingEmail: '',
    phone: '',
    address: '',
    city: '',
    state: 'FL',
    zip: '',
    terms: 'net30',
    creditLimit: 50000,
    rateCard: 'standard',
    accountOwner: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title="Add a customer"
      subtitle="Set the commercial basics — facilities and rate cards can follow"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              if (!form.name) {
                toast.err('A customer name is required');
                return;
              }
              setBusy(true);
              try {
                const payload = await api.post('/customers', { ...form, creditLimit: Number(form.creditLimit) });
                onCreated(payload.data);
                onClose();
              } catch (err) {
                toast.err('Could not add the customer', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Add customer
          </Button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 14 }}>
        <Field label="Company name" required><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Account type">
          <Select
            value={form.kind}
            onChange={(e) => set('kind', e.target.value)}
            options={[
              { value: 'shipper', label: 'Shipper' },
              { value: 'receiver', label: 'Receiver / DC' },
              { value: 'broker', label: 'Broker' },
              { value: 'carrier', label: 'Carrier' },
              { value: '3pl', label: '3PL' },
            ]}
          />
        </Field>
        <Field label="Billing email"><Input value={form.billingEmail} onChange={(e) => set('billingEmail', e.target.value)} /></Field>
        <Field label="Phone"><PhoneInput value={form.phone} onChange={(value) => set('phone', value)} /></Field>
        <Field label="Address" className="span-2"><Input value={form.address} onChange={(e) => set('address', e.target.value)} /></Field>
        <Field label="City"><Input value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field label="State"><Input value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} /></Field>
        <Field label="ZIP"><Input value={form.zip} onChange={(e) => set('zip', e.target.value)} /></Field>
        <Field label="Payment terms">
          <Select
            value={form.terms}
            onChange={(e) => set('terms', e.target.value)}
            options={[
              { value: 'due_on_receipt', label: 'Due on receipt' },
              { value: 'net15', label: 'Net 15' },
              { value: 'net30', label: 'Net 30' },
              { value: 'net45', label: 'Net 45' },
              { value: 'net60', label: 'Net 60' },
            ]}
          />
        </Field>
        <Field label="Credit limit"><Input className="mono" type="number" value={form.creditLimit} onChange={(e) => set('creditLimit', e.target.value)} /></Field>
        <Field label="Price list">
          <Select
            value={form.rateCard}
            onChange={(e) => set('rateCard', e.target.value)}
            options={[
              { value: 'standard', label: 'Standard' },
              { value: 'floor_load', label: 'Floor load' },
              { value: 'produce_reefer', label: 'Produce / reefer' },
              { value: 'crossdock', label: 'Cross dock' },
              { value: 'after_hours', label: 'After hours' },
            ]}
          />
        </Field>
        <Field label="Account owner" className="span-2"><Input value={form.accountOwner} onChange={(e) => set('accountOwner', e.target.value)} placeholder="Dana Whitfield" /></Field>
      </div>
    </Modal>
  );
}
